import { fromJS, Iterable } from 'immutable';
import { Dispatch } from 'redux';
import { JSONPath } from 'jsonpath-plus';

import { CallActionPayload, MethodResponse } from '../../types';
import { ContextAPI } from '../ImpactAiContextProvider';

interface ReduxGetOperation {
  reduxPath: string;
  contextKey: string;
}

interface ReduxSetOperation {
  value: any; // Can be a direct value or a template like "{{contextKey}}"
  reduxPath: string;
}

interface ReduxPayload {
  get?: ReduxGetOperation[];
  set?: ReduxSetOperation[];
}

// Store reference will be injected
let storeReference: any = null;

export const setReduxStoreReference = (store: any) => {
  storeReference = store;
};

// Deep convert Immutable structures to plain JS
const deepToJS = (obj: any): any => {
  if (!obj) {
    return obj;
  }

  // Check if it's an Immutable.js object
  if (Iterable.isIterable(obj)) {
    return obj.toJS();
  }

  // Check if it has a toJS method (for Records, etc.)
  if (obj && typeof obj.toJS === 'function') {
    return obj.toJS();
  }

  // If it's a plain object, recursively convert its properties
  if (obj && typeof obj === 'object' && obj.constructor === Object) {
    const result: any = {};
    Object.keys(obj).forEach((key) => {
      result[key] = deepToJS(obj[key]);
    });
    return result;
  }

  // If it's an array, recursively convert its elements
  if (Array.isArray(obj)) {
    return obj.map((item) => deepToJS(item));
  }

  // Return primitives as-is
  return obj;
};

export const redux = (
  payload: CallActionPayload,
  contextApi: ContextAPI,
  dispatch: Dispatch<any>
): MethodResponse => {
  const { get: getOperations = [], set: setOperations = [] } = payload as ReduxPayload;

  if (!storeReference) {
    throw new Error('Redux store reference not available');
  }

  // Process GET operations - read from Redux, write to context
  getOperations.forEach(({ reduxPath, contextKey }) => {
    try {
      const state = storeReference.getState();
      const stateAsJS = deepToJS(state.toJS());

      // Use JSONPath to query the state
      const jsonPathQuery = reduxPath.startsWith('$') ? reduxPath : `$.${reduxPath}`;
      const results = JSONPath({ path: jsonPathQuery, json: stateAsJS });
      const value = Array.isArray(results) && results.length > 0 ? results[0] : null;

      contextApi.setValue(contextKey, value);
    } catch (error) {
      // Silent error handling in production
    }
  });

  // Process SET operations - write to Redux
  setOperations.forEach(({ value, reduxPath }) => {
    try {
      if (value !== null && value !== undefined) {
        const pathParts = reduxPath.split('.');

        // Convert value to Immutable if needed
        const immutableValue =
          value !== null &&
          (Array.isArray(value) || (typeof value === 'object' && value.constructor === Object))
            ? fromJS(value)
            : value;

        // Dispatch a special action with the path and value
        dispatch({
          type: '@@IMPACT_AI/DIRECT_STATE_UPDATE',
          meta: {
            path: pathParts,
            value: immutableValue,
          },
        });
      }
    } catch (error) {
      // Silent error handling in production
    }
  });

  return {};
};
